<script setup lang="ts">
/**
 * A matrix of checkboxes: rows (people, roles, records) × columns that sit in
 * groups under a shared header — rights per person („Ordner“ / „Nachrichten“ ×
 * anzeigen · anlegen · ändern · löschen) and the like. Each label is written
 * once, in the head, instead of beside every checkbox.
 *
 * A real `<table>`: the head has a row of group headers spanning their columns
 * and a row of column headers, every row starts with a row header, every cell
 * holds an `NxCheckbox` named „<row> – <group> <column>“ (`cellLabel` to word it
 * differently). `isCellAvailable` leaves single cells empty — no checkbox, so
 * nothing to tick — where a row has no such column (a resource without that
 * action). The arrow keys move between the cells (skipping read-only and
 * unavailable ones), Home/End to the row's first and last, Space ticks.
 *
 * Controlled: `modelValue` maps row key → column key → ticked, a click emits
 * `update:modelValue` with a new object and `change` with the one cell — handle
 * `change` when ticking one cell has to tick or clear others. Column keys are
 * unique across groups. `row-label` replaces a row header's content,
 * `row-actions` adds a last column (a remove button).
 */
import { computed } from 'vue';
import NxCheckbox from '../Inputs/NxCheckbox.vue';
import {
  isMatrixCellChecked,
  setMatrixCell,
  type NxCheckboxMatrixChange,
  type NxCheckboxMatrixColumn,
  type NxCheckboxMatrixGroup,
  type NxCheckboxMatrixRow,
  type NxCheckboxMatrixValue,
} from './checkboxMatrix';

const props = withDefaults(
  defineProps<{
    rows: readonly NxCheckboxMatrixRow[];
    groups: readonly NxCheckboxMatrixGroup[];
    modelValue?: NxCheckboxMatrixValue;
    /** Every checkbox read-only (while saving). */
    disabled?: boolean;
    /** Names the table. */
    ariaLabel?: string;
    /** Header over the row labels („Person“); empty by default. */
    rowHeader?: string;
    /** Header of the `row-actions` column, for screen readers only. */
    actionsHeader?: string;
    /** A cell's accessible name; defaults to „<row> – <group> <column>“. */
    cellLabel?: (
      row: NxCheckboxMatrixRow,
      group: NxCheckboxMatrixGroup,
      column: NxCheckboxMatrixColumn
    ) => string;
    /** Whether a cell has a checkbox at all; every cell has one by default. */
    isCellAvailable?: (
      row: NxCheckboxMatrixRow,
      column: NxCheckboxMatrixColumn,
      group: NxCheckboxMatrixGroup
    ) => boolean;
  }>(),
  {
    modelValue: () => ({}),
    disabled: false,
    ariaLabel: undefined,
    rowHeader: '',
    actionsHeader: 'Aktionen',
    cellLabel: undefined,
    isCellAvailable: undefined,
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: Record<string, Record<string, boolean>>];
  /** The cell that was ticked or cleared. */
  change: [change: NxCheckboxMatrixChange];
}>();

defineSlots<{
  /** A row header's content in place of label and description. */
  'row-label'?: (p: { row: NxCheckboxMatrixRow }) => unknown;
  /** Buttons at the end of a row (remove). */
  'row-actions'?: (p: { row: NxCheckboxMatrixRow }) => unknown;
}>();

/** The columns left to right, each with its group and whether it opens one. */
const columns = computed(() =>
  props.groups.flatMap((group) =>
    group.columns.map((column, i) => ({ group, column, first: i === 0 }))
  )
);

function nameOf(
  row: NxCheckboxMatrixRow,
  group: NxCheckboxMatrixGroup,
  column: NxCheckboxMatrixColumn
): string {
  return props.cellLabel
    ? props.cellLabel(row, group, column)
    : `${row.label} – ${group.label} ${column.label}`;
}

function isAvailable(
  row: NxCheckboxMatrixRow,
  group: NxCheckboxMatrixGroup,
  column: NxCheckboxMatrixColumn
): boolean {
  return props.isCellAvailable ? props.isCellAvailable(row, column, group) : true;
}

function onToggle(
  row: NxCheckboxMatrixRow,
  group: NxCheckboxMatrixGroup,
  column: string,
  value: boolean
) {
  emit('update:modelValue', setMatrixCell(props.modelValue, row.key, column, value));
  emit('change', { row: row.key, group: group.key, column, value });
}

// ── Keyboard: the arrows walk the cells ─────────────────────────────────────
const STEPS: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
};

function checkboxAt(table: HTMLElement, row: number, col: number): HTMLButtonElement | null {
  const button = table.querySelector<HTMLButtonElement>(
    `[data-matrix-row="${row}"][data-matrix-col="${col}"] button[role="checkbox"]`
  );
  return button && !button.disabled ? button : null;
}

function onKeydown(event: KeyboardEvent) {
  const target = event.target as HTMLElement;
  if (!target.matches('button[role="checkbox"]')) return;
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  const cell = target.closest<HTMLElement>('[data-matrix-col]');
  const table = event.currentTarget as HTMLElement;
  if (!cell) return;
  const row = Number(cell.dataset.matrixRow);
  const col = Number(cell.dataset.matrixCol);
  const lastCol = columns.value.length - 1;
  let found: HTMLButtonElement | null = null;
  if (event.key === 'Home' || event.key === 'End') {
    const forward = event.key === 'Home';
    for (let c = forward ? 0 : lastCol; forward ? c <= lastCol : c >= 0; c += forward ? 1 : -1) {
      found = checkboxAt(table, row, c);
      if (found) break;
    }
  } else {
    const step = STEPS[event.key];
    if (!step) return;
    const vertical = event.key === 'ArrowUp' || event.key === 'ArrowDown';
    const delta = step[0];
    for (let n = 1; ; n++) {
      const r = vertical ? row + delta * n : row;
      const c = vertical ? col : col + delta * n;
      if (r < 0 || r >= props.rows.length || c < 0 || c > lastCol) break;
      found = checkboxAt(table, r, c);
      if (found) break;
    }
  }
  event.preventDefault();
  found?.focus();
}
</script>

<template>
  <div class="nx-checkbox-matrix">
    <table class="nx-checkbox-matrix__table" :aria-label="props.ariaLabel">
      <thead>
        <tr>
          <th v-if="props.rowHeader" class="nx-checkbox-matrix__corner" scope="col" rowspan="2">
            {{ props.rowHeader }}
          </th>
          <td v-else class="nx-checkbox-matrix__corner" rowspan="2" />
          <th
            v-for="group in props.groups"
            :key="group.key"
            class="nx-checkbox-matrix__group"
            scope="colgroup"
            :colspan="group.columns.length"
          >
            {{ group.label }}
          </th>
          <td v-if="$slots['row-actions']" class="nx-checkbox-matrix__actions" rowspan="2">
            <span class="nx-checkbox-matrix__sr-only">{{ props.actionsHeader }}</span>
          </td>
        </tr>
        <tr>
          <th
            v-for="{ group, column, first } in columns"
            :key="column.key"
            class="nx-checkbox-matrix__column"
            :class="{ 'is-group-start': first }"
            scope="col"
            :data-group="group.key"
          >
            {{ column.label }}
          </th>
        </tr>
      </thead>
      <tbody @keydown="onKeydown">
        <tr
          v-for="(row, r) in props.rows"
          :key="row.key"
          class="nx-checkbox-matrix__row"
          :class="{ 'is-disabled': row.disabled }"
          :data-row="row.key"
        >
          <th class="nx-checkbox-matrix__row-head" scope="row">
            <slot name="row-label" :row="row">
              <span class="nx-checkbox-matrix__label">{{ row.label }}</span>
              <span v-if="row.description" class="nx-checkbox-matrix__description">
                {{ row.description }}
              </span>
            </slot>
          </th>
          <td
            v-for="({ group, column, first }, c) in columns"
            :key="column.key"
            class="nx-checkbox-matrix__cell"
            :class="{ 'is-group-start': first }"
            :data-matrix-row="r"
            :data-matrix-col="c"
            :data-column="column.key"
          >
            <NxCheckbox
              v-if="isAvailable(row, group, column)"
              :model-value="isMatrixCellChecked(props.modelValue, row.key, column.key)"
              :aria-label="nameOf(row, group, column)"
              :disabled="props.disabled || row.disabled"
              @update:model-value="onToggle(row, group, column.key, $event)"
            />
          </td>
          <td v-if="$slots['row-actions']" class="nx-checkbox-matrix__actions">
            <slot name="row-actions" :row="row" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.nx-checkbox-matrix {
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  font-size: var(--nxt-font-size-sm);
}
.nx-checkbox-matrix__table {
  width: 100%;
  border-collapse: collapse;
  color: var(--nxt-fg);
}
.nx-checkbox-matrix__table th {
  font-weight: var(--nxt-font-weight-regular);
}
.nx-checkbox-matrix__corner {
  width: 100%;
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  color: var(--nxt-fg-muted);
  text-align: start;
  vertical-align: bottom;
}
.nx-checkbox-matrix__group {
  padding: 0 var(--nxt-space-sm) var(--nxt-space-2xs);
  border-inline-start: var(--nxt-hairline) solid var(--nxt-border-subtle);
  color: var(--nxt-fg-subtle);
  text-align: center;
  white-space: nowrap;
}
.nx-checkbox-matrix__column {
  padding: 0 var(--nxt-space-xs) var(--nxt-space-2xs);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  text-align: center;
  white-space: nowrap;
}
.nx-checkbox-matrix__row {
  border-bottom: var(--nxt-hairline) solid var(--nxt-border-subtle);
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-checkbox-matrix__row:hover {
  background: var(--nxt-table-row-hover-bg);
}
.nx-checkbox-matrix__row-head {
  padding: var(--nxt-space-xs) var(--nxt-space-sm) var(--nxt-space-xs) 0;
  text-align: start;
  vertical-align: middle;
}
.nx-checkbox-matrix__label,
.nx-checkbox-matrix__description {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-checkbox-matrix__description {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
.nx-checkbox-matrix__row.is-disabled .nx-checkbox-matrix__label {
  color: var(--nxt-fg-muted);
}
.nx-checkbox-matrix__cell {
  padding: var(--nxt-space-xs);
  text-align: center;
  vertical-align: middle;
}
.nx-checkbox-matrix__column.is-group-start,
.nx-checkbox-matrix__cell.is-group-start {
  border-inline-start: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-checkbox-matrix__actions {
  padding-inline-start: var(--nxt-space-xs);
  text-align: end;
  vertical-align: middle;
  white-space: nowrap;
}
.nx-checkbox-matrix__sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
@media (prefers-reduced-motion: reduce) {
  .nx-checkbox-matrix__row {
    transition: none;
  }
}
</style>
